import React from 'react';
import { EyeOffIcon } from 'lucide-react';
import { ConsultantContactList } from '../components/consultants/ConsultantContactList';
import { CandidateInquiryForm } from '../components/forms/CandidateInquiryForm';
import { ArchitecturalForm } from '../components/ui/ArchitecturalForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { PageHero } from '../components/ui/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { usePageMeta } from '../hooks/usePageMeta';
const expectations = [{
  title: 'A clear explanation',
  description: 'When we approach you about a role, we explain the organization, the mandate, and why we believe it may interest you — within the limits of any confidentiality.'
}, {
  title: 'Your consent first',
  description: 'We never present your background to an organization without discussing the opportunity with you and receiving your agreement.'
}, {
  title: 'Honest feedback',
  description: 'Whether an opportunity progresses or not, we aim to give you candid, useful feedback.'
}, {
  title: 'Respect for your time',
  description: 'We only contact you about opportunities that align with the experience and aspirations you have shared.'
}];
const guidance = [{
  title: 'Lead with outcomes',
  description: 'Describe what changed under your leadership — the decisions you made and their results — rather than listing responsibilities.'
}, {
  title: 'Show scope clearly',
  description: 'Indicate the size of the teams, budgets, markets, or organizations you have led, so your level of responsibility is easy to understand.'
}, {
  title: 'Explain transitions',
  description: 'A sentence on why you moved between roles helps reviewers understand your career narrative.'
}, {
  title: 'Be specific about what is next',
  description: 'Share the kinds of roles, organizations, and locations you would genuinely consider — and those you would not.'
}];
export function Candidates() {
  usePageMeta('For Candidates', 'Experienced leaders can introduce themselves confidentially to Aurevane Search Partners for consideration in relevant C-suite, board, and senior executive searches.');
  return <>
      <PageHero label="For Candidates" title="Experienced Leaders Deserve Meaningful Opportunities." intro="Introduce your background, leadership experience, and aspirations to our team in confidence, for consideration in relevant current and future searches." visual={<ArchitecturalForm variant="steps" tone="dark" data-id="element-588" data-mp="2vomv0t09xgja" />} data-id="element-587" data-mp="27z0wm7rq75cc">
        <ButtonLink to="#introduce" size="lg" data-id="element-589" data-mp="2vr7wvi0knwh1">
          Introduce Yourself
        </ButtonLink>
        <ButtonLink to="/opportunities" variant="outline-light" size="lg" data-id="element-590" data-mp="2qwbtjlpvxmom">
          Explore Leadership Opportunities
        </ButtonLink>
      </PageHero>

      <section className="bg-porcelain py-24 lg:py-28" data-id="element-591" data-mp="3jfr6c0vcyfap">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-592" data-mp="3eo0s3jtav7e0">
          <div className="lg:col-span-6" data-id="element-593" data-mp="3em66e3a7fb8v">
            <SectionHeading label="Candidate engagement" title="A considered conversation, not a transaction" data-id="element-594" data-mp="1164srmqsbmxj" />
            <p className="mt-8 max-w-prose text-lg leading-relaxed text-plum/75" data-id="element-595" data-mp="165hbpwejc9uu">
              Many of the leaders we speak with are not actively looking. They are open to the right conversation at the right time. We take time to understand your experience and what you want from your next chapter, so that any opportunity we raise is relevant.
            </p>
          </div>
          <div className="lg:col-span-5 lg:col-start-8" data-id="element-596" data-mp="1p2s9fckiqv86">
            <h3 className="text-xl font-semibold text-aubergine" data-id="element-597" data-mp="1p17evmy7fcab">Executive-level opportunities</h3>
            <p className="mt-4 leading-relaxed text-plum/75" data-id="element-598" data-mp="1tw4xnllad00e">
              Our searches focus on senior appointments: chief executives and C-suite officers, board and non-executive directors, executive committee members, and senior functional and divisional leaders.
            </p>
            <ButtonLink to="/opportunities" variant="ghost" arrow className="mt-6" data-id="element-599" data-mp="27ubfxtdr4rey">
              View Leadership Opportunities
            </ButtonLink>
          </div>
        </div>
      </section>

      <section className="on-dark bg-aubergine py-20 text-porcelain lg:py-24" data-id="element-600" data-mp="1p7ghczjw3daj">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:px-10" data-id="element-601" data-mp="23vumjh4s3du9">
          <div className="lg:col-span-4" data-id="element-602" data-mp="1yxjaczr0gr8v">
            <EyeOffIcon className="h-6 w-6 text-copper-light" aria-hidden="true" data-id="element-603" data-mp="1yzw1a2eqyby2" />
            <h2 className="mt-6 text-3xl font-semibold leading-tight tracking-tight" data-id="element-604" data-mp="1b8qknl0zffmp">Professional discretion</h2>
          </div>
          <p className="max-w-prose text-lg leading-relaxed text-porcelain/80 lg:col-span-7 lg:col-start-6" data-id="element-605" data-mp="16bi0xg06r3jc">
            Exploring a new role while in a senior position requires care. Your details are held confidentially, are never published, and are only shared with an organization after we have discussed the specific opportunity with you and you have agreed.
          </p>
        </div>
      </section>

      <section className="bg-limestone py-24 lg:py-28" data-id="element-606" data-mp="2z5zgpnjbp7h2">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-10" data-id="element-607" data-mp="2z57tazzvqg1v">
          <div data-id="element-608" data-mp="3463x7dk8e27a">
            <SectionHeading label="What to expect" title="How we work with candidates" data-id="element-609" data-mp="346nin493b8q2" />
            <ul className="mt-10 space-y-8" data-id="element-610" data-mp="2q6mgllh39v90">
              {expectations.map(e => <li key={e.title} className="border-t border-plum/15 pt-5" data-id="element-611" data-mp="2u7xyt5xmve76">
                  <h3 className="font-display text-lg font-semibold text-aubergine" data-id="element-612" data-mp="3hvz1vzc1m2f7">{e.title}</h3>
                  <p className="mt-2 leading-relaxed text-plum/75" data-id="element-613" data-mp="3mw3jxtsln57j">{e.description}</p>
                </li>)}
            </ul>
          </div>
          <div data-id="element-614" data-mp="393x5cc53h6a7">
            <SectionHeading label="Guidance" title="Presenting your executive experience" data-id="element-615" data-mp="392ckje8l11pj" />
            <ul className="mt-10 space-y-8" data-id="element-616" data-mp="3d3o00qxqs6k0">
              {guidance.map(g => <li key={g.title} className="border-t border-plum/15 pt-5" data-id="element-617" data-mp="4onvbrh88ary">
                  <h3 className="font-display text-lg font-semibold text-aubergine" data-id="element-618" data-mp="9hpam0lcicc2">{g.title}</h3>
                  <p className="mt-2 leading-relaxed text-plum/75" data-id="element-619" data-mp="jbmhp8188ezt">{g.description}</p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <section id="introduce" className="bg-porcelain py-24 lg:py-28" data-id="element-620" data-mp="ab6k2f76022v">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-621" data-mp="5cv89yxb7hi0">
          <div className="lg:col-span-4" data-id="element-622" data-mp="5f9264g96nnz">
            <SectionHeading label="Register your interest" title="Introduce yourself" intro="Share your background for consideration in relevant searches. There is no specific vacancy attached — we will contact you if an appropriate opportunity arises." data-id="element-623" data-mp="e1sfzujveeo" />
          </div>
          <div className="rounded-2xl border border-plum/10 bg-white p-6 sm:p-10 lg:col-span-8" data-id="element-624" data-mp="3itcf0x81p5s8">
            <CandidateInquiryForm data-id="element-625" data-mp="3ds6l4w2nfch4" />
          </div>
        </div>
      </section>

      <section className="border-t border-plum/10 bg-porcelain py-20" data-id="element-626" data-mp="38umv6ywl9b9c">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-627" data-mp="2ypx09czlr7em">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-628" data-mp="2ypey5zq9dyys">
            <h2 className="text-2xl font-semibold text-aubergine" data-id="element-629" data-mp="33n6qguutfn3i">Contact an Executive Search Consultant</h2>
            <ButtonLink to="/opportunities" variant="ghost" arrow data-id="element-630" data-mp="38omcap4thxmy">
              Explore Leadership Opportunities
            </ButtonLink>
          </div>
          <div className="mt-8" data-id="element-631" data-mp="2fzykeapkca0f">
            <ConsultantContactList data-id="element-632" data-mp="2g0qcvcnv5rmh" />
          </div>
        </div>
      </section>
    </>;
}